import {
  DiscoveryAssistantMode,
  listDiscoveryMessages,
  type DiscoveryMessage,
} from '@/entities/discovery';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { searchCreatorPendingAtom } from './mutation-atom';

export const discoveryMessagesAtom = atom<Array<DiscoveryMessage>>([]);
const discoveryMessagesPaginationAtom = atom({
  nextCursor: null as string | null,
  hasMore: false,
});

export const useDiscoveryMessagesActions = () => {
  const setMessages = useSetAtom(discoveryMessagesAtom);

  const appendMessages = useCallback(
    (messages: Array<DiscoveryMessage>) => {
      setMessages((prev) => {
        return [...messages, ...prev];
      });
    },
    [setMessages],
  );

  const replaceMessages = useCallback(
    (messages: Array<DiscoveryMessage>) => {
      setMessages((prev) => {
        return [...messages, ...(prev?.slice(messages.length) || [])];
      });
    },
    [setMessages],
  );

  const updateSearchMessageHistoryId = useCallback(
    (messageId: string, searchHistoryId: string) => {
      setMessages((prev) =>
        prev.map((message) => {
          if (
            message.message_id !== messageId ||
            message.role !== 'assistant' ||
            message.assistant_mode !== DiscoveryAssistantMode.SEARCH
          ) {
            return message;
          }

          return {
            ...message,
            search_history_id: searchHistoryId,
          };
        }),
      );
    },
    [setMessages],
  );

  return {
    appendMessages,
    replaceMessages,
    updateSearchMessageHistoryId,
  };
};

export function useDiscoveryMessagesQuery(discoverySessionId: string) {
  const initializedRef = useRef(false);
  const searchPending = useAtomValue(searchCreatorPendingAtom);
  const messages = useAtomValue(discoveryMessagesAtom);
  const setMessages = useSetAtom(discoveryMessagesAtom);
  const pagination = useAtomValue(discoveryMessagesPaginationAtom);
  const setPagination = useSetAtom(discoveryMessagesPaginationAtom);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  const fetchInitialMessages = useCallback(async () => {
    const res = await listDiscoveryMessages(discoverySessionId);
    setMessages(res.messages);
    setPagination({
      nextCursor: res.next_cursor,
      hasMore: res.has_more,
    });
    initializedRef.current = true;
  }, [discoverySessionId, setMessages, setPagination]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (searchPending) return;
    fetchInitialMessages();
  }, [searchPending, fetchInitialMessages]);

  const fetchNextPage = useCallback(async () => {
    if (searchPending) return;
    if (!pagination.hasMore || isFetchingNextPage) return;

    setIsFetchingNextPage(true);
    const response = await listDiscoveryMessages(discoverySessionId, {
      cursor: pagination.nextCursor ?? undefined,
    });
    setIsFetchingNextPage(false);

    setMessages((prev) => [...(prev || []), ...response.messages]);
    setPagination({
      nextCursor: response.next_cursor,
      hasMore: response.has_more,
    });
  }, [
    discoverySessionId,
    searchPending,
    isFetchingNextPage,
    pagination.hasMore,
    pagination.nextCursor,
    setMessages,
    setPagination,
  ]);

  return {
    messages,
    fetchNextPage,
    hasNextPage: pagination.hasMore,
    isFetchingNextPage,
  };
}

export function useDiscoveryMessages() {
  const messages = useAtomValue(discoveryMessagesAtom);
  return messages;
}
